import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { adminService } from "../services/admin.service";

export function useAdminData(isAuthenticated: boolean) {
  const queryClient = useQueryClient();

  // Queries với cache tối ưu để loại bỏ giật lag
  const categoriesQuery = useQuery({
    queryKey: ["admin-categories"],
    queryFn: adminService.getCategoriesAdmin,
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 5, // Cache 5 phút
    refetchOnWindowFocus: false,
  });

  const productsQuery = useQuery({
    queryKey: ["admin-products"],
    queryFn: adminService.getProducts,
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });

  const contactsQuery = useQuery({
    queryKey: ["admin-contacts"],
    queryFn: adminService.getContacts,
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });

  // Optimistic updates hoặc invalidate thông minh
  const createCategoryMutation = useMutation({
    mutationFn: adminService.createCategory,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["admin-categories"] }),
  });

  const updateCategoryMutation = useMutation({
    mutationFn: ({ id, formData }: { id: string; formData: FormData }) =>
      adminService.updateCategory(id, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-categories"] });
    },
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: adminService.deleteCategory,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["admin-categories"] }),
  });

  const createProductMutation = useMutation({
    mutationFn: (formData: FormData) => adminService.createProduct(formData),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["admin-products"] }),
  });

  const updateProductMutation = useMutation({
    mutationFn: ({ id, formData }: { id: string; formData: FormData }) =>
      adminService.updateProduct(id, formData),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["admin-products"] }),
  });

  const deleteProductMutation = useMutation({
    mutationFn: adminService.deleteProduct,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["admin-products"] }),
  });

  const updateContactStatusMutation = useMutation({
    mutationFn: ({ id, isResolved }: { id: string; isResolved: boolean }) =>
      adminService.updateContactStatus(id, isResolved),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["admin-contacts"] }),
  });

  const deleteContactMutation = useMutation({
    mutationFn: adminService.deleteContact,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["admin-contacts"] }),
  });

  return {
    categories:
      categoriesQuery.data?.data?.items || categoriesQuery.data?.data || [],
    products: productsQuery.data?.data?.items || productsQuery.data?.data || [],
    contacts: contactsQuery.data?.data?.items || contactsQuery.data?.data || [],
    isLoading:
      categoriesQuery.isLoading ||
      productsQuery.isLoading ||
      contactsQuery.isLoading,
    createCategory: createCategoryMutation,
    updateCategory: updateCategoryMutation,
    deleteCategory: deleteCategoryMutation,
    createProduct: createProductMutation,
    updateProduct: updateProductMutation,
    deleteProduct: deleteProductMutation,
    updateContactStatus: updateContactStatusMutation,
    deleteContact: deleteContactMutation,
  };
}
